import { useState } from "react";
import { toast } from "sonner";
import { X, GripVertical, Save, Trash2, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { inputCls } from "./utils";

// Board settings editor — add / rename / reorder stages, toggle Syncro
// mirroring, and (when enabled) kick a one-shot backfill so existing
// board clients get pushed as Syncro customers + tickets right away.

export function StageEditor({ board, onClose, onSaved, partnerCode }) {
  const [stages, setStages] = useState(board.stages);
  const [syncroEnabled, setSyncroEnabled] = useState(!!board.syncro_sync_enabled);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  function move(i, dir) {
    const next = [...stages];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setStages(next.map((s, idx) => ({...s, order: idx + 1})));
  }
  function update(i, patch) { setStages(stages.map((s, idx) => idx === i ? {...s, ...patch} : s)); }
  function remove(i) { setStages(stages.filter((_, idx) => idx !== i)); }
  function add() { setStages([...stages, { key: "", label: "New stage", color: "#64748b", order: stages.length + 1 }]); }

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.patch(`/commissions/migrations/${partnerCode}`, {
        stages, syncro_sync_enabled: syncroEnabled,
      });
      toast.success("Board settings saved");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  }

  async function syncNow() {
    setSyncing(true);
    try {
      const { data } = await api.post(`/commissions/migrations/${partnerCode}/sync-syncro`);
      toast.success(`Synced ${data.synced}/${data.attempted} to Syncro${data.failed ? ` · ${data.failed} failed` : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="stage-editor">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-xl tracking-tight">Board stages</h3>
            <p className="text-sm text-slate-400 mt-1">Add, rename or reorder. Clients in a deleted stage roll back to the first stage.</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-2 mb-4">
          {stages.map((s, i) => (
            <div key={s.key || s.id || `stage-${i}`} className="flex items-center gap-2 p-2 rounded-lg border border-slate-800 bg-slate-950/40" data-testid={`stage-row-${i}`}>
              <GripVertical className="w-4 h-4 text-slate-600" />
              <input
                type="color" value={s.color || "#64748b"}
                onChange={(e) => update(i, { color: e.target.value })}
                className="w-8 h-8 rounded bg-transparent border border-slate-800"
              />
              <input
                data-testid={`stage-label-${i}`}
                value={s.label} onChange={(e) => update(i, { label: e.target.value })}
                className={inputCls + " flex-1"}
              />
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 disabled:opacity-30">↑</button>
              <button onClick={() => move(i, 1)} disabled={i === stages.length - 1} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 disabled:opacity-30">↓</button>
              <button
                data-testid={`stage-delete-${i}`}
                onClick={() => remove(i)}
                className="p-1.5 rounded hover:bg-slate-800 text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          data-testid="stage-add"
          onClick={add}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-700 hover:border-slate-500 text-sm text-slate-400 hover:text-slate-100 mb-4"
        >
          <Plus className="w-4 h-4" /> Add stage
        </button>

        <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-4 mb-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              data-testid="syncro-sync-toggle"
              type="checkbox"
              checked={syncroEnabled}
              onChange={(e) => setSyncroEnabled(e.target.checked)}
              className="mt-1"
            />
            <div className="text-sm">
              <div className="font-medium text-slate-100">Mirror to Syncro</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Every new client on this board becomes a Syncro customer + ticket. Board notes get pushed as <b>private ticket comments</b>. Requires Syncro credentials on the tenant.
              </div>
            </div>
          </label>
          {syncroEnabled && (
            <button
              type="button"
              data-testid="syncro-backfill-now"
              onClick={syncNow}
              disabled={syncing}
              className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-slate-800 hover:bg-slate-800 disabled:opacity-60"
            >
              {syncing ? "Syncing…" : "Sync existing clients now"}
            </button>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm">Cancel</button>
          <button
            data-testid="stage-editor-save"
            onClick={save} disabled={busy}
            className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save board settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
